import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { adminAPI } from '../services/api';
import LanguageSwitcher from './LanguageSwitcher';
import styles from './Header.module.css';

const Header = ({ dateStr }) => {
  const { t } = useTranslation();
  const { user, logout, isAdmin } = useAuth();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);

  useEffect(() => {
    if (!adminOpen) return;
    setAdminLoading(true);
    adminAPI.getUsers()
      .then((res) => setAdminUsers(res.data))
      .finally(() => setAdminLoading(false));
  }, [adminOpen]);

  return (
    <>
      <Box className={styles.header}>
        <Box className={styles.titleGroup}>
          <Typography className={styles.appTitle}>
            {t('header.title')}
          </Typography>
          <Typography className={styles.dateStr}>
            {dateStr}
          </Typography>
        </Box>
        <Box className={styles.userArea}>
          <LanguageSwitcher />
          <Box
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            className={styles.userTrigger}
          >
            <Typography className={styles.userLabel}>
              {t('header.greeting', { name: user?.username })}
            </Typography>
            <PersonOutlinedIcon className={styles.userIcon} />
          </Box>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { className: styles.menuPaper } }}
          >
            {isAdmin && (
              <MenuItem
                onClick={() => { setMenuAnchor(null); setAdminOpen(true); }}
                className={styles.menuItem}
              >
                {t('header.menu.admin')}
              </MenuItem>
            )}
            <MenuItem
              onClick={() => { setMenuAnchor(null); setAccountOpen(true); }}
              className={styles.menuItem}
            >
              {t('header.menu.account')}
            </MenuItem>
            <MenuItem
              onClick={() => { setMenuAnchor(null); logout(); }}
              className={styles.menuItem}
            >
              {t('header.menu.logout')}
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Account dialog */}
      <Dialog
        open={accountOpen}
        onClose={() => setAccountOpen(false)}
        PaperProps={{ className: `${styles.dialogPaper} ${styles.dialogPaperSm}` }}
      >
        <DialogTitle className={styles.dialogTitle}>
          {t('header.account.title')}
        </DialogTitle>
        <DialogContent className={styles.accountContent}>
          {[
            { label: t('header.account.username'), value: user?.username },
            { label: t('header.account.email'), value: user?.email },
          ].map(({ label, value }) => (
            <Box key={label}>
              <Typography className={styles.fieldLabel}>
                {label}
              </Typography>
              <Typography className={styles.fieldValue}>
                {value}
              </Typography>
            </Box>
          ))}
        </DialogContent>
      </Dialog>

      {/* Admin dialog */}
      <Dialog
        open={adminOpen}
        onClose={() => setAdminOpen(false)}
        PaperProps={{ className: `${styles.dialogPaper} ${styles.dialogPaperLg}` }}
      >
        <DialogTitle className={styles.dialogTitle}>
          {t('header.admin.title')}
        </DialogTitle>
        <DialogContent className={styles.adminContent}>
          {adminLoading ? (
            <Typography className={styles.adminLoading}>
              {t('common.loading')}
            </Typography>
          ) : (
            <>
              {/* Header row */}
              <Box className={`${styles.adminGrid} ${styles.adminHeaderRow}`}>
                {['username', 'email', 'active', 'roles'].map((col) => (
                  <Typography key={col} className={styles.adminColLabel}>
                    {t(`header.admin.columns.${col}`)}
                  </Typography>
                ))}
              </Box>

              {/* Data rows */}
              {adminUsers.map((u) => (
                <Box key={u.id} className={`${styles.adminGrid} ${styles.adminDataRow}`}>
                  <Typography className={styles.cell}>
                    {u.username}
                  </Typography>
                  <Typography className={`${styles.cell} ${styles.cellEmail}`}>
                    {u.email}
                  </Typography>
                  <Typography className={`${styles.cell} ${u.enabled ? '' : styles.cellMuted}`}>
                    {u.enabled ? t('common.yes') : t('common.no')}
                  </Typography>
                  <Typography className={styles.cell}>
                    {u.roles.join(', ')}
                  </Typography>
                </Box>
              ))}

              {adminUsers.length === 0 && (
                <Typography className={styles.adminEmpty}>
                  {t('header.admin.empty')}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Header;
